
import { Box, useMediaQuery, MenuItem, Select, Typography,CircularProgress  } from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { BASE_URL } from '../../../components/BaseUrl';
import { decryptToken } from "../../../Utlis/Encryption";
import { useSelector } from "react-redux";
import graphicon from "../../../assets/Dashboard/graphicon.svg";
import { showUnderDevelopment } from '../../../components/alerts';
import CustomText, { textStyles } from '../../../components/CustomText';

// 👇 import your empty image (adjust path/name)
import emptyscreen from "../../../assets/Dashboard/emptyscreen.svg"
const MONTH_ABBR = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const ClientGrowthmap = () => {
  const { t } = useTranslation();
const currentYear = new Date().getFullYear();
  const rawEncryptedToken = localStorage.getItem("token");
  const token = rawEncryptedToken ? decryptToken(rawEncryptedToken) : null;

  // AE / Executive ID from Redux (adjust selector if needed)
  const executiveId = useSelector((s) => s.auth?.user?.id);

  // const currentYear = new Date().getFullYear();
  const [clientYear, setClientYear] = useState(currentYear);

  const [currentChartData, setCurrentChartData] = useState([]);
  const [mpLoading, setMpLoading] = useState(false);
  const [mpError, setMpError] = useState(null);

  const isMobile = useMediaQuery('(max-width:768px)');

  useEffect(() => {
    if (!executiveId || !token || !clientYear) return;

    const url = `${BASE_URL}/account-executive/${executiveId}/statistics/monthly-performance?year=${clientYear}`;
    setMpLoading(true);
    setMpError(null);

    fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    })
      .then(async (res) => {
        const json = await res.json();
        if (!res.ok || json?.error) {
          throw new Error(json?.message || `Request failed: ${res.status}`);
        }

        const trends = json?.data?.monthly_trends ?? [];
        const chart = trends.map((m) => ({
          name: MONTH_ABBR[(m.month ?? 1) - 1] || m.month_name || String(m.month),
          value: Number(m.companies_acquired ?? 0),
          companies_acquired: Number(m.companies_acquired ?? 0),
          commissions_earned: Number(m.commissions_earned ?? 0),
        }));

        setCurrentChartData(chart);
      })
      .catch((err) => {
        console.error("Monthly performance fetch error:", err);
        setMpError(err.message || "Failed to load monthly performance");
        setCurrentChartData([]);
      })
      .finally(() => setMpLoading(false));
  }, [executiveId, token, clientYear]);

  // chart helpers
  const hasBars = currentChartData.some(d => d.value > 0);
  const maxY = hasBars ? Math.max(...currentChartData.map(d => d.value)) : 0;
  const yMaxRounded = Math.max(10, Math.ceil(maxY / 50) * 50); // nice steps
  const years = Array.from({ length:100}, (_, i) => 2015 + i);
  
  return (
    <Box sx={{ px: isMobile ? 1 : 2, py: 2, backgroundColor: 'white', border: "1px solid #091E4224", borderRadius: 2, boxShadow: '0px 0px 10px rgba(0,0,0,0.05)' }}>
      <Box sx={{ display: 'flex', flexWrap: "wrap", justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography sx={{ fontSize: 15, fontFamily: 'Nunito Sans, sans-serif', fontWeight: 700, color: '#212121' }}>
          {t('dashboard.client_growth_map')}
        </Typography>

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Select
            size="small"
            value={clientYear}
            onChange={(e) => setClientYear(e.target.value)}
       sx={{
              width: 90,
              fontSize: "13px",
              height: 32,
              "& .MuiSelect-select": {
                padding: "4px 8px",
                fontWeight: 600,
                textAlign: "center",
              },
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  maxHeight: 200,
                  "& .MuiMenuItem-root": {
                    fontSize: "13px",
                    padding: "6px 10px",
                  },
                },
              },
            }}
          >
          
               {years.map((yr) => (
      <MenuItem
        key={yr}
        value={yr}
        disabled={yr > currentYear} // 🚀 disables future years
      >
        {yr}
      </MenuItem>
    ))}
          </Select>

  
        </Box>
      </Box>

      {/* Chart or Empty Image */}
      {mpLoading ? (
          <Box
    sx={{
      height: 300,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
     <CircularProgress sx={{ color: '#006EC2' }} size={40} thickness={4} />
  </Box>
      ) : !hasBars ? (
        <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <Box sx={{ display: 'flex',flexDirection:"column" ,justifyContent: 'center', alignItems: 'center', height: 300 }}>
          <img src={emptyscreen} alt="No Data" style={{ width: 200,  }} />
            <CustomText
    sx={{
      ...textStyles.h1,
      fontWeight: 500,
      fontSize: '16px',
      color: '#003149',
      pt:1,
    }}
  >
  {t('empty.no_user')}
  </CustomText>

        </Box>        </Box>
      ) : (
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={currentChartData} barSize={35} barGap={2} barCategoryGap={2}>
            <CartesianGrid vertical={false} stroke="#E0E0E0" />
            <XAxis
              dataKey="name"
              interval={0}
              tick={{
                angle: isMobile ? -45 : 0,
                fontSize: isMobile ? 11 : 14,
                fill: '#2C384C', fontWeight: 500, fontFamily: 'Poppins, sans-serif'
              }}
            />
            <YAxis
              domain={[0, yMaxRounded || 50]}
              ticks={Array.from({ length: Math.floor((yMaxRounded || 50) / 10) + 1 }, (_, i) => i * 10)}
              tick={{ fontSize: 12, fill: '#030229', fontFamily: 'Nunito Sans, sans-serif' }}
            />
           <Tooltip
  contentStyle={{ backgroundColor: 'transparent', border: 'none', boxShadow: 'none' }}
  cursor={{ fill: 'transparent' }}
  formatter={(value) => [`${value}`, t('value')]}
 />


            <Bar dataKey="value" radius={[10, 10, 0, 0]} fill="url(#colorBlue)" />
            <defs>
              <linearGradient id="colorBlue" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#1A7FFC" stopOpacity={1} />
                <stop offset="100%" stopColor="#49BFFF" stopOpacity={1} />
              </linearGradient>
            </defs>
          </BarChart>
        </ResponsiveContainer>
      )}

      {mpError && (
        <div style={{ marginTop: 8, fontSize: 12, color: '#b00020' }}>
          {mpError}
        </div>
      )}
    </Box>
  );
};

export default ClientGrowthmap;



